import apiClient from '../api/api';
import { API_ENDPOINTS } from '../api/api.config';

export interface Group {
  _id: string;
  groupId?: string;
  parent_group_id: string;
  group_id: string;
  group_name: string;
  group_description: string;
  users_count?: number;
  roles_count?: number;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface GroupMetrics {
  total_groups: number;
  groups_in_use: number;
  total_users_with_groups: number;
}

export interface CreateGroupData {
  group_id: string;
  group_name: string;
  parent_group_id: string;
  group_description: string;
}

export interface UpdateGroupData {
  group_id: string;
  group_name: string;
  parent_group_id: string;
  group_description: string;
}

export interface GroupListParams {
  skip: number;
  limit: number;
  group_name?: string;
  group_id?: string;
  _id?: string;
}

export interface GroupListResponse {
  success: boolean;
  code: number;
  description?: string;
  data: Group[];
  metrics_data?: GroupMetrics;
  total_count?: number;
}

export interface GroupStatsResponse {
  success: boolean;
  code: number;
  data: {
    total_groups: number;
    active_groups: number;
    inactive_groups: number;
  };
}

class GroupsService {
  async getGroups(params: GroupListParams): Promise<GroupListResponse> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.GROUPS.LIST, params);
      return response.data;
    } catch (error) {
      console.error('Error fetching groups:', error);
      throw error;
    }
  }

  async getGroupById(id: string): Promise<Group> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.GROUPS.GET_BY_ID?.(id));
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching group ${id}:`, error);
      throw error;
    }
  }

  async createGroup(groupData: CreateGroupData): Promise<Group> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.GROUPS.CREATE, groupData);
      return response.data.data; // Assuming API returns {success, code, data: group}
    } catch (error) {
      console.error('Error creating group:', error);
      throw error;
    }
  }

  async updateGroup(group_id: string, groupData: UpdateGroupData): Promise<Group> {
    try {
      const response = await apiClient.put(API_ENDPOINTS.GROUPS.UPDATE(group_id), groupData);
      return response.data.data; // Assuming API returns {success, code, data: group}
    } catch (error) {
      console.error(`Error updating group ${group_id}:`, error);
      throw error;
    }
  }

  async deleteGroup(group_id: string): Promise<void> {
    try {
      await apiClient.delete(API_ENDPOINTS.GROUPS.DELETE(group_id));
    } catch (error) {
      console.error(`Error deleting group ${group_id}:`, error);
      throw error;
    }
  }

  async getGroupStats(): Promise<GroupStatsResponse> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.GROUPS.GROUP_REPORT);
      return response.data;
    } catch (error) {
      console.error('Error fetching group stats:', error);
      throw error;
    }
  }
  async getGroupsList(): Promise<any> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.GROUPS.GROUP_DROPDOWN);
      return response.data;
    } catch (error) {
      console.error('Error fetching groups list:', error);
      throw error;
    }
  }
  async userGroupRoleMapping(user: any): Promise<any> {
  try {
    const response = await apiClient.post(API_ENDPOINTS.GROUPS.GROUP_ROLE_MAPPING, user, );
    return response.data; // { success, code, data: { totalUsers, users } }
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
}
async userGroupRoleUpdate(user: any): Promise<any> {
  try {
    const response = await apiClient.put(API_ENDPOINTS.GROUPS.GROUP_ROLE_MAPPING + `/${user.group_key}`, user, );
    return response.data; // { success, code, data: { totalUsers, users } }
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
}
async userGroupRoleGet(user: any): Promise<any> {
  try {
    const response = await apiClient.get(API_ENDPOINTS.GROUPS.GROUP_ROLE_MAPPING + `/${user}` );
    return response.data; // { success, code, data: { totalUsers, users } }
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
}
}

export const groupsService = new GroupsService();
export default groupsService;